import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { CloudOff, RefreshCw, Check } from 'lucide-react';
import { useOnline, usePendingSync } from '@/hooks/useConnection';
import { useT } from '@/i18n/useT';

/**
 * A small floating pill that tells the user where their data stands: offline,
 * syncing queued changes, or just synced. Mounted once in the app shell.
 */
export function ConnectionStatus() {
  const t = useT();
  const online = useOnline();
  const pending = usePendingSync();
  const [justSynced, setJustSynced] = useState(false);
  const prev = useRef({ online, pending });

  useEffect(() => {
    const was = prev.current;
    const hadWork = !was.online || was.pending > 0;
    const nowClear = online && pending === 0;
    prev.current = { online, pending };
    if (hadWork && nowClear) {
      setJustSynced(true);
      const timer = setTimeout(() => setJustSynced(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [online, pending]);

  const state = !online ? 'offline' : pending > 0 ? 'syncing' : justSynced ? 'synced' : null;

  const label =
    state === 'offline'
      ? pending > 0
        ? t('conn.offlinePending', { n: pending })
        : t('conn.offline')
      : state === 'syncing'
        ? t('conn.syncing', { n: pending })
        : t('conn.synced');

  const tone =
    state === 'synced'
      ? 'bg-brand text-white'
      : state === 'offline'
        ? 'bg-surface-2 text-content'
        : 'bg-surface text-content';

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-50 flex justify-center pt-[max(env(safe-area-inset-top),8px)]"
      aria-live="polite"
    >
      <AnimatePresence>
        {state && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium shadow-[var(--shadow-card)] ${tone}`}
          >
            {state === 'offline' && <CloudOff size={13} aria-hidden />}
            {state === 'syncing' && <RefreshCw size={13} className="animate-spin" aria-hidden />}
            {state === 'synced' && <Check size={13} aria-hidden />}
            {label}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
